import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import LaunchButtonRow from '../common/LaunchButtonRow';
import { FAVORITE_KIND, resolveFavoriteKind } from '../cards/CardVisuals';
import { getCollectionFamilyCardIds, getCollectionFavoriteCardIds } from '../../utils/collections';
import { accessContextFor, launchHomeFor } from '../../utils/collectionFamily';
import { canWriteCard } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { PIN_KIND } from '../../services/favorites';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';

/**
 * The path a launched card lands on: the blank new-item form for that card.
 */
export const newItemPath = (collectionId, cardId) => (
	`/collections/${normalizeId(collectionId)}/items/new/${normalizeId(cardId)}`
);

const tierRank = scope => (scope === FAVORITE_KIND.COLLECTION ? 0 : 1);

/**
 * The favorite cards a caller may launch from, ordered for a launch row:
 * collection favorites first (the collection's own standard leads), then the
 * user's own, name order within each tier.
 *
 * Only WRITABLE member cards qualify — a favorite the caller cannot add items
 * to would launch straight into a 403. A card that is both a collection and a
 * user favorite reads as the collection tier (the pin the user cannot remove
 * is the one worth showing), exactly as the picker rows resolve it.
 *
 * At a PARENT collection a favorite may name a sub-collection's card: it is
 * offered when the caller can write there, and `home` is that
 * sub-collection — where the item is made (utils/collectionFamily.js).
 *
 * `isFavoriteCard` is `cardId => boolean`, already bound to the collection.
 * Returns [{ id, card, scope, home }].
 */
export function selectFavoriteCards({ collection, cards, isFavoriteCard }) {
	if (!collection) return [];
	const memberIds = getCollectionFamilyCardIds(collection, cards || []);
	const collectionFavoriteIds = getCollectionFavoriteCardIds(collection);
	const isUserFavorite = typeof isFavoriteCard === 'function' ? isFavoriteCard : null;
	return (cards || [])
		.map(card => {
			const id = normalizeId(card?._id);
			if (!id || !memberIds.has(id)) return null;
			if (!canWriteCard(accessContextFor(collection, id), id)) return null;
			const scope = resolveFavoriteKind(id, collectionFavoriteIds, isUserFavorite);
			return scope ? { id, card, scope, home: launchHomeFor(collection, id) } : null;
		})
		.filter(Boolean)
		.sort((a, b) => (
			tierRank(a.scope) - tierRank(b.scope)
			|| (a.card?.name || '').localeCompare(b.card?.name || '')
		));
}

/**
 * Favorite cards as one-click launchers (see LaunchButtonRow for the row rule).
 *
 * `onLaunch(cardId, card, home)` overrides the default navigation so a host can do
 * its own housekeeping first — the Add item modal closes itself before it
 * navigates. Renders nothing when the caller has no launchable favorite.
 */
export default function FavoriteCardButtons({
	collectionId,
	collection,
	cards,
	isFavoriteCard,
	onLaunch,
	max,
	getPopupContainer,
	// Offer a Home pin toggle on each button (the collection home does; the
	// Add item modal, a place to launch from, does not).
	pinToHome = false,
}) {
	const navigate = useNavigate();
	const favoritesCtx = useOptionalFavorites();
	const canPin = pinToHome && typeof favoritesCtx?.togglePin === 'function';

	const favorites = useMemo(
		() => selectFavoriteCards({ collection, cards, isFavoriteCard }),
		[collection, cards, isFavoriteCard]
	);

	const entries = useMemo(() => favorites.map(({ id, card, scope, home }) => ({
		key: id,
		// A sub-collection's card launches there; its Home pin would launch
		// here, so it offers none.
		pin: canPin && !home ? {
			pinned: favoritesCtx.isPinned(PIN_KIND.ADD_ITEM, id, collectionId),
			onToggle: () => favoritesCtx.togglePin(PIN_KIND.ADD_ITEM, { targetId: id, collectionId: normalizeId(collectionId) }),
		} : undefined,
		record: card,
		kind: 'card',
		label: card?.name || '(untitled card)',
		// A sub-collection's card: the button says where the item is made (a
		// parent's card used here makes its item HERE, and is not qualified).
		collection: home || undefined,
		collectionNote: home ? (identity => `Adds the item in ${identity.name}`) : undefined,
		scope,
		onLaunch: () => {
			if (typeof onLaunch === 'function') {
				onLaunch(id, card, home);
				return;
			}
			navigate(newItemPath(home?._id || collectionId, id));
		},
	})), [favorites, onLaunch, navigate, collectionId, canPin, favoritesCtx]);

	if (!entries.length) return null;

	return (
		<LaunchButtonRow
			entries={entries}
			max={max}
			ariaLabel="Favorite cards"
			getPopupContainer={getPopupContainer}
		/>
	);
}
